<template>
    <b-card
        no-body
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>
                    {{ $t('org.billing.Monthly by Date') }}
                </h4>
            </div>
            <a
                data-action="collapse"
                :class="{ rotate: isCollapsed }"
                @click="triggerCollapse"
                class="mr-2"
            >
                <feather-icon
                    icon="ChevronDownIcon"
                    size="16"
                />
            </a>
        </div>
        <div
            class="card-body"
            v-if="noData"
            style="text-align:center;"
        >
            {{ $t("org.message.No Data") }}
        </div>
        <div
            v-else
            ref="REF_DYNAMICSEARCH_MONTHLY_DAILY_LINE"
            class="ml-2"
        >
            <VsSvgLine
                v-if="width > 0"
                circle
                marginLeft="auto"
                :data="chartData"
                :xKey="xKey"
                :yKeys="yKeys"
                :lineColor="lineColor"
                :width="width"
                :height="height"
                :labelRotate="0"
                :tickSize="0.7"
                :strokeWidth="3"
                :yGrid="6"
                :xAxis="true"
                :yAxis="true"
                xLabel
                yLabel
                :labelColor="themeColors.currentColor"
                legendDirection="right-bottom"
                :legendSize="10"
                @mouseover:point="hoverPoint"
                @mouseleave.native="outPoint"
            >
                <template v-slot:x-axis="{ item }">
                    <template v-if="chartData.length >= 28 && item.index % 7 === 0">
                        {{ formattedDate(lang, item.label, 'day') }}
                    </template>
                    <template v-else-if="chartData.length < 28 && chartData.length >= 14 && item.index % 3 === 0">
                        {{ formattedDate(lang, item.label, 'day') }}
                    </template>
                    <template v-else-if="chartData.length < 14 && item.index % 1 === 0">
                        {{ formattedDate(lang, item.label, 'day') }}
                    </template>
                </template>
                <template v-slot:y-axis="{ item }">
                    {{ __toKFormat(item.label) }}
                </template>
                <template v-slot:tooltip="{ item }">
                    <g v-if="tooltipData.index > -1">
                        <rect
                            :x="tooltipData.index < chartData.length / 2 ? item.x + 10 : item.x - (tooltipWidth) - 10"
                            :y="height - item.y < height / 2 ? item.y - (yKeys.length * 17 + 35) - padding : padding + 5"
                            :fill="backgroundColor"
                            :height="(yKeys.length) * 17 + padding"
                            :width="tooltipWidth"
                            opacity="0.9"
                            rx="5"
                            ry="5"
                        ></rect>
                        <text
                            font-size="12"
                            font-weight="bold"
                            :fill="themeColors.currentColor"
                            :text-anchor="tooltipData.index < chartData.length / 2 ? 'start' : 'end'"
                            :x="tooltipData.index < chartData.length / 2 ? item.x + 15 : item.x - 15"
                            :y="height - item.y < height / 2 ? item.y - ((yKeys.length) * 17) - padding * 2 : padding * 2"
                        >
                            {{ item.label }}
                        </text>
                        <template v-for="(value, index) in yKeys">
                            <text :key="`t-${index}`"
                                v-if="typeof item.data[value] === 'number'"
                                font-size="14"
                                :font-weight="value === item.yKey ? 'bold' : undefined"
                                :fill="themeColors.currentColor"
                                :text-anchor="tooltipData.index < chartData.length / 2 ? 'start' : 'end'"
                                :x="tooltipData.index < chartData.length / 2 ? item.x + 15 : item.x - 15"
                                :y="height - item.y < height / 2 ? item.y - (yKeys.length * 17) - padding * 2 + ((index + 1) * 17) : padding * 2 + ((index + 1) * 17)"
                            >
                                {{ value }} : {{ $_getDollar(item.data[value], 2) }}
                            </text>
                        </template>
                    </g>
                </template>
            </VsSvgLine>
            <template v-if="costOfAfterThisMonth > 0">
                <p :style="{'color': themeColors.warning }" class="text-right mr-2 mb-0">
                    <small>
                        * {{ $t('org.billing.costNotExpressedInGraph').replace('{cost}', $_getDollar(costOfAfterThisMonth).replace(' ','')) }}
                    </small>
                </p>
            </template>
        </div>
        <b-collapse
            v-model="visible"
            :visible="visible"
            class="card-content"
        >
            <b-table-simple>
                <b-tbody>
                    <b-tr v-for="(item, index) in finalData" :key="index">
                        <b-td style="word-break:break-all;">
                            <b-form-checkbox
                                v-model="checked"
                                switch
                                inline
                                :value="item.key"
                                @change="changeSwitch"
                            >
                                <span class="switch-icon-left">
                                    <feather-icon
                                        icon="CheckIcon"/>
                                </span>
                                <label @click="handleLabelClick(item.key)">
                                    {{ item.key }}
                                </label>
                            </b-form-checkbox>
                        </b-td>
                        <b-td class="text-right" style="width:30%;">
                            {{ filterDataMap[item.key] ? $_getDollar(filterDataMap[item.key]): undefined }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-collapse>
    </b-card>
</template>

<script>
import { getCurrentInstance, computed, ref, toRefs, onUpdated, onMounted, onUnmounted, watch } from '@vue/composition-api'
import useCommonData from '@/composables/useCommonData'
import { VsSvgLine } from '@/libs/vuesign/chart'
import formattedDate from '@/utils/formattedDate'
export default {
    components: {
        VsSvgLine
    },
    props: {
        fullData: {
            type: Array,
            default: function () {
                return []
            }
        },
        filterData: {
            type: Object,
            default: function () {
                return {
                    open: false,
                    monthlyCost: [],
                    dailyCost: {}
                }
            }
        },
        startDate: {
            type: Number,
            default: 0
        },
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        },
        theme: {
            type: String
        }
    },
    setup (props, context) {
        const { __toKFormat } = useCommonData()
        const { themeColors, theme, filterData, fullData } = toRefs(props)
        const vm = getCurrentInstance().proxy
        const d3 = vm.$d3
        const monthlyCost = computed(() => filterData.value.monthlyCost)
        const dailyCost = computed(() => filterData.value.dailyCost)
        const width = ref(0)
        const height = 250
        const padding = 20
        const visible = ref(false)
        const isCollapsed = ref(false)
        const checked = ref(monthlyCost.value.slice().map(item => item.key))
        const lang = computed(() => vm.$i18n.locale)
        // chart
        const xKey = 'day'
        const yKeys = computed(() => monthlyCost.value.filter(item => dailyCost.value[item.key] && dailyCost.value[item.key].length).map(item => item.key))
        const tooltipWidth = computed(() => {
            const width = (Math.max.apply(null, yKeys.value.map(key => key.length)) * 10)
            return width < 200 ? 200 : width
        })
        const backgroundColor = computed(() => themeColors.value[theme.value])
        const colorScale = computed(() => d3.scaleOrdinal().domain(fullData.value.map(item => item.cost)).range(themeColors.value.range.slice().reverse()))
        const lineColor = computed(() => {
            return yKeys.value.reduce((r, v, i) => {
                r[v] = colorScale.value(i)
                return r
            }, {})
        })
        const chartData = computed(() => {
            const xKeyFlagMap = yKeys.value.reduce((accumulator, currentValue) => {
                dailyCost.value[currentValue].forEach(item => {
                    if (item.key.substring(0, 7) === currentValue) {
                        const xKey = Number(item.key.substring(8, 10))
                        if (!accumulator[xKey]) accumulator[xKey] = {}
                        accumulator[xKey][currentValue] = {
                            cost: item.cost
                        }
                    }
                })
                return accumulator
            }, {})
            const list = []
            for (let i = 0; i < 31; i++) {
                const item = {}
                let isItem = false
                yKeys.value.forEach(month => {
                    const day = i + 1
                    item.day = day
                    if (xKeyFlagMap[day] && xKeyFlagMap[day][month]) {
                        item[month] = xKeyFlagMap[day][month].cost
                        isItem = true
                    } else {
                        item[month] = null
                    }
                })
                if (isItem) {
                    list.push(item)
                }
            }
            return list
        })
        // table
        const filterDataMap = computed(() => {
            return monthlyCost.value.reduce((accumlator, currentValue) => {
                accumlator[currentValue.key] = currentValue.cost
                return accumlator
            }, {})
        })
        const noData = computed(() => !fullData.value.length)
        const triggerCollapse = () => {
            visible.value = !visible.value
            isCollapsed.value = !isCollapsed.value
        }
        const costOfAfterThisMonth = computed(() => {
            let sum = 0
            yKeys.value.forEach(key => {
                sum += dailyCost.value[key].reduce((accumlator, currentValue) => {
                    if (currentValue.key.indexOf(key) < 0) {
                        accumlator += currentValue.cost
                    }
                    return accumlator
                }, 0)
            })
            return sum
        })
        const finalData = computed(() => {
            return fullData.value.map(item => {
                return {
                    ...item,
                    cost: filterDataMap.value[item.key]
                }
            })
        })
        const handleLabelClick = (value) => {
            if (checked.value.length === 1 && checked.value[0] === value) {
                checked.value = fullData.value.map(item => item.key)
            } else {
                checked.value = [value]
            }
            context.emit('filter:indexes', checked.value)
        }
        const changeSwitch = (value) => {
            if (!value.length) {
                checked.value = fullData.value.map(v => v.key)
            }
            checked.value = value.length ? value : fullData.value.map(v => v.key)
            context.emit('filter:indexes', checked.value)
        }

        // tooltip
        const tooltipData = ref({})
        const hoverPoint = ({ event, data }) => {
            tooltipData.value = data
        }
        const outPoint = () => {
            tooltipData.value = {}
        }
        // resize
        const REF_DYNAMICSEARCH_MONTHLY_DAILY_LINE = ref(null)
        const resize = () => {
            if (REF_DYNAMICSEARCH_MONTHLY_DAILY_LINE.value) {
                width.value = REF_DYNAMICSEARCH_MONTHLY_DAILY_LINE.value.getBoundingClientRect().width
            }
        }
        onUpdated(resize)
        onMounted(() => {
            vm.$nextTick(() => {
                resize()
                window.addEventListener('resize', resize)
            })
        })
        onUnmounted(() => {
            window.removeEventListener('resize', resize)
        })
        // watch
        watch(fullData, (value, oldValue) => {
            checked.value = value.map(v => v.key)
        }, {
            deep: true
        })
        return {
            REF_DYNAMICSEARCH_MONTHLY_DAILY_LINE,
            width,
            height,
            padding,
            xKey,
            yKeys,
            visible,
            isCollapsed,
            __toKFormat,
            backgroundColor,
            lineColor,
            chartData,
            noData,
            tooltipData,
            hoverPoint,
            outPoint,
            triggerCollapse,
            tooltipWidth,
            costOfAfterThisMonth,
            checked,
            filterDataMap,
            finalData,
            handleLabelClick,
            changeSwitch,
            formattedDate,
            lang
        }
    }
}
</script>

<style>

</style>
